import { observer } from 'mobx-react';
import { CommandBarButton } from '@fluentui/react/lib/Button';
import { Callout } from '@fluentui/react/lib/Callout';
import { DirectionalHint } from '@fluentui/react/lib/ContextualMenu';
import { PersonaSize } from '@fluentui/react/lib/Persona';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { getRTL } from '@fluentui/react/lib/Utilities';
import { getGuid } from 'owa-guid';
import PersonaControl from 'owa-persona/lib/components/PersonaControl';
import Message from 'owa-service/lib/contract/Message';
import { SurfaceActionItem } from 'owa-surface-actions';
import * as React from 'react';
import {
    getLikeCountDisplayString,
    getMessageLikersDisplayString,
} from '../utils/getMessageLikersDisplayString';
import {
    useLivePersonaCard,
    PersonaCardBehaviorProps,
} from 'owa-persona/lib/components/PersonaCardBehavior';

const styles = require('./CustomLikeButton.scss');
const rpstyles = require('./ItemActions.scss');
const classNames = require('classnames');

export interface LikeButtonProps {
    message: Message;
    surfaceActionItem: SurfaceActionItem;
}

export interface LikeButtonState {
    isMouseOverButton: boolean;
    isMouseOverLikersList: boolean;
}

@observer
export default class CustomLikeButton extends React.Component<LikeButtonProps, LikeButtonState> {
    constructor(props: any) {
        super(props);
        this.state = {
            isMouseOverButton: false,
            isMouseOverLikersList: false,
        };
    }

    public IsLikersListShown(): boolean {
        return this.state.isMouseOverLikersList;
    }

    render() {
        const { surfaceActionItem, message } = this.props;

        const shouldShowLikersList =
            message.LikeCount &&
            message.Likers &&
            (this.state.isMouseOverButton || this.state.isMouseOverLikersList);

        const likeButtonId = 'likeButtonId' + getGuid();

        return (
            <TooltipHost content={surfaceActionItem.ariaLabel}>
                <CommandBarButton
                    id={likeButtonId}
                    role={'menuitem'}
                    ariaLabel={surfaceActionItem.ariaLabel}
                    ariaDescription={getMessageLikersDisplayString(message)}
                    iconProps={surfaceActionItem.iconProps}
                    styles={{
                        root: classNames(
                            rpstyles.surfaceActionsCommandBarButton,
                            styles.likeIconButtonCustom,
                            shouldShowLikersList && styles.likeCustomButtonActive
                        ),
                        label: message.LikeCount && styles.likeCustomButtonLabel,
                        rootDisabled: rpstyles.disabledReplyActionButton,
                        icon: rpstyles.enabledIconButton,
                        iconDisabled: rpstyles.disabledIconButton,
                        menuIcon: rpstyles.commandBarMenuIcon,
                    }}
                    onClick={this.onLikeClicked}
                    onMouseEnter={this.handleMouseOver}
                    onMouseOver={this.handleMouseOver}
                    onMouseLeave={this.handleMouseLeave}
                    text={getLikeCountDisplayString(message)}
                />
                {shouldShowLikersList && (
                    <Callout
                        className={styles.callout}
                        onDismiss={this.onDismissCallOut}
                        target={'#' + likeButtonId}
                        isBeakVisible={false}
                        setInitialFocus={false}
                        directionalHint={
                            getRTL()
                                ? DirectionalHint.bottomLeftEdge
                                : DirectionalHint.bottomRightEdge
                        }>
                        {
                            <div
                                className={classNames(styles.scrollContainer, 'customScrollBar')}
                                onMouseEnter={this.handleLikersListMouseOver}
                                onMouseOver={this.handleLikersListMouseOver}
                                onMouseLeave={this.handleLikersListMouseLeave}>
                                <div className={styles.personaCollection}>
                                    {message.Likers.map(result => (
                                        <LPCWrapper
                                            key={result.EmailAddress}
                                            emailAddress={result.EmailAddress}
                                            name={result.Name}
                                            mailBoxType={result.MailboxType}
                                            disableHover={true}
                                            clientScenario="Liker">
                                            <PersonaControl
                                                className={styles.persona}
                                                key={result.EmailAddress}
                                                name={result.Name}
                                                emailAddress={result.EmailAddress}
                                                size={PersonaSize.size32}
                                                showPersonaDetails={true}
                                                mailboxType={result.MailboxType}
                                            />
                                        </LPCWrapper>
                                    ))}
                                </div>
                            </div>
                        }
                    </Callout>
                )}
            </TooltipHost>
        );
    }

    private onDismissCallOut = () => {
        this.setState({
            ...this.state,
            isMouseOverButton: false,
            isMouseOverLikersList: false,
        });
    };

    private onLikeClicked = (evt: React.MouseEvent<unknown>) => {
        const event = evt as React.MouseEvent<HTMLElement>;
        this.props.surfaceActionItem.onClick(event, this.props.surfaceActionItem);
        event.stopPropagation();
    };

    private handleMouseOver = () => {
        if (this.state.isMouseOverButton) {
            return;
        }

        this.setState({ isMouseOverButton: true });
    };

    private handleMouseLeave = () => {
        if (!this.state.isMouseOverButton) {
            return;
        }

        this.setState({ isMouseOverButton: false });
    };

    private handleLikersListMouseOver = () => {
        if (this.state.isMouseOverLikersList) {
            return;
        }

        this.setState({ isMouseOverLikersList: true });
    };

    private handleLikersListMouseLeave = () => {
        if (!this.state.isMouseOverLikersList) {
            return;
        }

        this.setState({ isMouseOverLikersList: false });
    };
}

function LPCWrapper(props: PersonaCardBehaviorProps & { children: React.ReactNode }) {
    const PersonaCardBehavior = useLivePersonaCard(props);
    return <PersonaCardBehavior>{props.children}</PersonaCardBehavior>;
}
